import { supabase } from '../../lib/supabase';
import type { Branch, uuid, ProfileWithBranches } from '../../types/database';
import { handleSupabaseError, isPostgrestError } from '../../utils/errorHandling';

interface RawProfileData {
  id: string;
  username: string;
  email: string | null;
  role: 'staff' | 'admin' | 'manager';
  invoice_prefix: string;
  branch_id: string | null;
  avatar_url: string | null;
  mobile_no: string | null;
  created_at: string;
  updated_at: string;
  last_used_branch_id: string | null;
  last_used_branch: Branch | Branch[] | null;
  profile_branches: Array<{
    profile_id: string;
    branch_id: string;
    created_at: string;
    updated_at: string;
    branch: Branch | Branch[];
  }>;
}

export async function fetchUserProfile(userId: uuid): Promise<ProfileWithBranches> {
  try {
    const toBranchObject = (possibleArray: Branch | Branch[] | null): Branch | null => {
      if (!possibleArray) return null;
      if (Array.isArray(possibleArray)) {
        return possibleArray[0] ?? null;
      }
      return possibleArray;
    };

    const { data, error } = await supabase
      .from('profiles')
      .select(`
        id,
        username,
        email,
        role,
        invoice_prefix,
        branch_id,
        avatar_url,
        mobile_no,
        created_at,
        updated_at,
        last_used_branch_id,
        last_used_branch:last_used_branch_id (
          id,
          name,
          invoice_prefix,
          address,
          is_active,
          created_at,
          updated_at
        ),
        profile_branches (
          profile_id,
          branch_id,
          created_at,
          branch:branch_id (
            id,
            name,
            invoice_prefix,
            address,
            is_active,
            created_at,
            updated_at
          )
        )
      `)
      .eq('id', userId)
      .single();

    if (error) {
      throw isPostgrestError(error) ? handleSupabaseError(error) : error;
    }

    if (!data) {
      throw new Error('Profile not found');
    }

    const rawData = data as unknown as RawProfileData;

    const fixedLastUsedBranch = toBranchObject(rawData.last_used_branch);

    const fixedProfileBranches = rawData.profile_branches.map(pb => {
      const fixedBranch = toBranchObject(pb.branch);
      return {
        ...pb,
        branch: fixedBranch,
      };
    });

    const profileWithBranches: ProfileWithBranches = {
      ...rawData,
      last_used_branch: fixedLastUsedBranch,
      profile_branches: fixedProfileBranches.map(pb => ({
        profile_id: pb.profile_id,
        branch_id: pb.branch_id,
        created_at: pb.created_at,
        updated_at: pb.updated_at,
        branch: pb.branch!,
      }))
    };

    return profileWithBranches;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
}

export async function updateLastUsedBranch(userId: uuid, branchId: uuid): Promise<void> {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ last_used_branch_id: branchId })
      .eq('id', userId);

    if (error) {
      throw isPostgrestError(error) ? handleSupabaseError(error) : error;
    }
  } catch (error) {
    console.error('Error updating last used branch:', error);
    throw error;
  }
} 